import { buildUrl, BACKEND_CFG, API_BASE, API_BASE_RU } from '../config/api';
import publicSite from '../config/publicSite';
import { measurementHeaders } from '../lib/measurementContext';
import { detectDeviceTimezone } from '../utils/timezone';

// Глобальный обработчик ошибок аутентификации
let onAuthError = null;
export const setAuthErrorHandler = (handler) => {
  onAuthError = handler;
};

export function isTrustedAuthTarget(value) {
  try {
    const pageOrigin = typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : '';
    const target = new URL(String(value || ''), pageOrigin || API_BASE);
    const trustedOrigins = [pageOrigin, API_BASE, API_BASE_RU]
      .filter(Boolean)
      .map((item) => new URL(String(item), pageOrigin || API_BASE).origin);
    return trustedOrigins.includes(target.origin);
  } catch {
    return false;
  }
}

// Базовый API клиент
export async function apiFetch(path, init = {}) {
  const url = buildUrl(path);
  const trustedAuthTarget = isTrustedAuthTarget(url);

  const headers = new Headers(init.headers || {});
  // Stale caller headers must not bypass the current browser's privacy choice.
  [...headers.keys()].filter((key) => /^x-noesa-(utm-|acquisition-|attribution$|measurement$|analytics-consent$|landing$)/i.test(key)).forEach((key) => headers.delete(key));
  if (trustedAuthTarget) {
    for (const [key, value] of Object.entries(measurementHeaders())) headers.set(key, value);
    const deviceTimezone = detectDeviceTimezone();
    if (!init.noAuth && deviceTimezone) headers.set('X-Noesa-Timezone', deviceTimezone);
  } else {
    [...headers.keys()].filter((key) => key.toLowerCase().startsWith('x-noesa-')).forEach((key) => headers.delete(key));
  }
  const skipAuth = init.noAuth === true;
  if (!trustedAuthTarget) {
    headers.delete("Authorization");
    headers.delete("Proxy-Authorization");
    headers.delete("X-Requested-With");
  }
  // Всегда пробуем добавить Bearer, если токен есть (даже при cookie‑режиме)
  const stored = (()=>{ try { return JSON.parse(window.localStorage.getItem('ai_diary_auth')||'{}'); } catch { return {}; } })();
  let bearerToken = init.token || stored?.user?.token;
  if (!bearerToken) {
    try { bearerToken = window.localStorage.getItem('ai_diary_token') || window.localStorage.getItem('token') || undefined; } catch {}
  }
  if (!skipAuth && trustedAuthTarget && bearerToken && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${bearerToken}`);
  }
  // Нередко WAF/CSRF ждут этот заголовок
  if (trustedAuthTarget && BACKEND_CFG.tokenKind === "cookie" && !headers.has("X-Requested-With")) {
    headers.set("X-Requested-With", "XMLHttpRequest");
  }

  // Auto-JSON for plain objects
  let body = init.body;
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
  if (body && !isFormData && typeof body === 'object') {
    if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
    body = JSON.stringify(body);
  }

  // Optional timeout support
  const timeoutMs = typeof init.timeoutMs === 'number' ? init.timeoutMs : null;
  let controller = null;
  let timeoutId = null;
  if (timeoutMs && !init.signal && typeof AbortController !== 'undefined') {
    controller = new AbortController();
    timeoutId = setTimeout(() => { try { controller.abort(); } catch {} }, timeoutMs);
  }

  const defaultInit = {
    ...init,
    mode: init.mode || "cors",
    credentials: skipAuth || !trustedAuthTarget
      ? "omit"
      : (init.credentials || (BACKEND_CFG.tokenKind === "bearer" ? "omit" : "include")),
    headers,
    body,
    ...(controller ? { signal: controller.signal } : {}),
  };

  try {
    const response = await fetch(url, defaultInit);
    try { if (timeoutId) clearTimeout(timeoutId); } catch {}
    
    // Обработка ошибок аутентификации
    if (response.status === 401) {
      if (init.suppressAuthError || skipAuth || !trustedAuthTarget || (typeof window !== 'undefined' && window.location.pathname.startsWith('/s/'))) {
        // Публичные вызовы (share) — без редиректа
        throw new Error('Unauthorized');
      }
      try { window.localStorage.removeItem('ai_diary_auth'); } catch {}
      try { window.dispatchEvent(new Event('noesa:auth-cleared')); } catch {}
      const p = typeof window !== 'undefined' ? window.location.pathname : '';
      const publicRoots = ['/welcome', '/ai-planner', '/pricing', '/code', '/login', '/privacy', '/terms', '/cookies', '/news'];
      if (!publicSite.resolvePublicRoute(p) && p !== '/' && !publicRoots.some((r) => p.startsWith(r))) {
        window.location.replace('/');
      }
      if (onAuthError) { onAuthError(); }
      throw new Error('Unauthorized');
    }
    
    // Обработка других ошибок
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const rawDetail = errorData?.detail;
      let msg = (errorData && (rawDetail || errorData.error || errorData.message)) || `HTTP ${response.status}: ${response.statusText}`;
      if (msg && typeof msg === 'object') {
        msg = msg.message || msg.code || JSON.stringify(msg);
      }
      const err = new Error(String(msg));
      err.status = response.status;
      err.code = errorData?.code || errorData?.detail?.code;
      err.detail = errorData?.detail;
      err.payload = errorData;
      err.url = url;
      throw err;
    }
    
    return response;
  } catch (error) {
    try { if (timeoutId) clearTimeout(timeoutId); } catch {}
    if (error && error.name === 'AbortError') {
      const abortedByCaller = !!init.signal;
      const err = new Error(abortedByCaller ? 'Request aborted' : 'Timeout: request took too long');
      err.code = abortedByCaller ? 'aborted' : 'timeout';
      err.url = url;
      throw err;
    }
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error('Network error: Unable to connect to server');
    }
    throw error;
  }
}

// Специализированные методы
export const api = {
  // GET запрос
  get: (path, params = {}) => {
    const url = new URL(buildUrl(path));
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.append(key, value);
      }
    });
    return apiFetch(url.pathname + url.search);
  },
  
  // POST запрос
  post: (path, data = {}) => {
    return apiFetch(path, {
      method: 'POST',
      body: data,
    });
  },
  
  // PUT запрос
  put: (path, data = {}) => {
    return apiFetch(path, {
      method: 'PUT',
      body: data,
    });
  },

  patch: (path, data = {}) => {
    return apiFetch(path, {
      method: 'PATCH',
      body: data,
    });
  },
  
  // DELETE запрос
  delete: (path) => {
    return apiFetch(path, {
      method: 'DELETE',
    });
  },
  
  // Загрузка файлов
  upload: (path, formData) => {
    return apiFetch(path, {
      method: 'POST',
      headers: {}, // Убираем Content-Type для FormData
      body: formData,
    });
  },
  
  // Стриминг ответов
  stream: (path, init = {}) => {
    return apiFetch(path, {
      ...init,
      headers: {
        'Accept': 'text/event-stream',
        'Cache-Control': 'no-cache',
        ...(init.headers || {}),
      },
    });
  },
};

// Утилиты для работы с ответами
export const parseResponse = async (response) => {
  const contentType = response.headers.get('content-type');
  
  if (contentType && contentType.includes('application/json')) {
    return await response.json();
  }
  
  if (contentType && contentType.includes('text/')) {
    return await response.text();
  }
  
  return await response.blob();
};

// Хуки для работы с API (для использования в компонентах)
export const useApi = () => {
  return {
    get: async (path, params) => {
      const response = await api.get(path, params);
      return await parseResponse(response);
    },
    
    post: async (path, data) => {
      const response = await api.post(path, data);
      return await parseResponse(response);
    },
    
    put: async (path, data) => {
      const response = await api.put(path, data);
      return await parseResponse(response);
    },
    
    delete: async (path) => {
      const response = await api.delete(path);
      return await parseResponse(response);
    },
    
    upload: async (path, formData) => {
      const response = await api.upload(path, formData);
      return await parseResponse(response);
    },
  };
};
